import { useCallback, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { MdClose } from "react-icons/md";
import Hls from "hls.js";

export type VideoPlayerModalProps = {
  open: boolean;
  onClose: () => void;
  src: string;
  title?: string;
};

export default function VideoPlayerModal({
  open,
  onClose,
  src,
  title,
}: VideoPlayerModalProps) {
  const hlsRef = useRef<Hls | null>(null);

  // Callback ref fires after the <video> DOM node actually mounts inside the
  // Portal — unlike useEffect which runs before the Portal flushes to the DOM.
  const videoCallbackRef = useCallback(
    (video: HTMLVideoElement | null) => {
      if (!video) {
        hlsRef.current?.destroy();
        hlsRef.current = null;
        return;
      }

      if (Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS support
        video.src = src;
        video.play().catch(() => {});
      }
    },
    [src],
  );

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4 focus:outline-none">
          <div className="relative w-full max-w-5xl">
            <Dialog.Close
              onClick={onClose}
              className="absolute -top-10 right-0 flex items-center gap-1.5 text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              <MdClose className="w-5 h-5" />
              <span className="text-sm font-medium">Close</span>
            </Dialog.Close>
            <Dialog.Title className="text-white font-semibold text-lg mb-3 font-['Figtree']">
              {title ?? ""}
            </Dialog.Title>
            <video
              ref={videoCallbackRef}
              controls
              className="w-full rounded-xl bg-black aspect-video"
              playsInline
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
